import type { ChildAppConfig } from '@shared/app-config'
import type { CmdLogSubAction, LoggerApi, LogLevelValue, LogSource } from '@shared/logging/types'
import { IFrameMessageType, sendIframeMessage } from '@shared/iframe-messages'
import { captureCallerStack, LogLevel, serializeError } from '@shared/logging/types'

export type { LoggerApi }

function send(subAction: CmdLogSubAction, data: Record<string, unknown>, source: LogSource, callerStack?: string) {
  sendIframeMessage(IFrameMessageType.CmdLog, {
    subAction,
    data,
    source,
    timestamp: Date.now(),
    callerStack,
  })
}

// Frames: caller → info/warn/error → logWithStack → send
const LOG_FRAMES_TO_SKIP = 3
// Frames: caller → captureException/captureMessage → send
const CAPTURE_FRAMES_TO_SKIP = 2

export function createLoggerAPI(config: ChildAppConfig): LoggerApi {
  const source: LogSource = {
    layer: 'child',
    resourceId: config.context?.resourceId,
    resourceType: config.context?.resourceType,
    nestingDepth: config.context?.nestingDepth ?? 0,
  }

  function log(level: LogLevelValue, message: string, data?: Record<string, unknown>) {
    send('log', { message, level, extra: data }, source)
  }

  function logWithStack(level: LogLevelValue, message: string, data?: Record<string, unknown>) {
    send('log', { message, level, extra: data }, source, captureCallerStack(LOG_FRAMES_TO_SKIP))
  }

  return {
    debug: (message, data) => log(LogLevel.Debug, message, data),
    info: (message, data) => logWithStack(LogLevel.Info, message, data),
    warn: (message, data) => logWithStack(LogLevel.Warning, message, data),
    error: (message, data) => logWithStack(LogLevel.Error, message, data),

    captureException(error, extra) {
      send('captureException', { ...serializeError(error), extra }, source, captureCallerStack(CAPTURE_FRAMES_TO_SKIP))
    },

    captureMessage(message, level, extra) {
      send('captureMessage', { message, level: level ?? LogLevel.Info, extra }, source, captureCallerStack(CAPTURE_FRAMES_TO_SKIP))
    },

    addBreadcrumb(breadcrumb) {
      send('addBreadcrumb', { ...breadcrumb, timestamp: breadcrumb.timestamp ?? Date.now() }, source)
    },

    setContext(name, data) {
      send('setContext', { name, data }, source)
    },

    setTag(key, value) {
      send('setTag', { key, value }, source)
    },
  }
}
